import { useCallback } from 'react';
import copyToClipboard from 'copy-to-clipboard';

import { Modals } from 'Services/Modals';
import { SnackBar } from 'Services/SnackBar';
import { isSystemShareSupported } from 'Shared/Share/utils';
import LinkIcon from 'Shared/svg/link-bold.svg';
import ShareIcon from 'Shared/svg/share.svg';

import { trackCopyLinkClick } from '../../biEvents';
import { ArrowIcon } from '../components/ArrowIcon';
import { CopyIcon } from '../components/CopyIcon';
import { LinkTitle } from '../components/LinkTitle';

type PropsT = {
  availableGiftTotal: number;
  shareLink: string;
  onMobileShare: (url: string) => void;
  isMpArticle?: boolean;
};

export const ShareItem: React.FunctionComponent<PropsT> = ({
  availableGiftTotal,
  shareLink,
  onMobileShare,
  isMpArticle,
}) => {
  const handleCopy = useCallback(() => {
    copyToClipboard(shareLink);
    trackCopyLinkClick(availableGiftTotal);
    Modals.removeCurrentModal();
    SnackBar.push({ content: 'Share Link Copied' });
  }, [availableGiftTotal, shareLink]);

  const handleShare = useCallback(() => {
    onMobileShare(shareLink);
  }, [shareLink, onMobileShare]);

  const subTitle = isMpArticle
    ? 'The recipient cannot see the article content unless they are a service subscriber, or it is a free article'
    : 'The recipient will see a paywall unless they are a Seeking Alpha subscriber';

  if (isSystemShareSupported) {
    return (
      <button
        aria-label="share-link"
        className="flex w-full items-center justify-between"
        data-test-id="share-link-button"
        type="button"
        onClick={handleShare}
      >
        <div className="flex items-center">
          <span className="rounded-full bg-orange-60 p-10">
            <ShareIcon className="size-28 fill-share-card-bg-2" />
          </span>

          <LinkTitle
            availableCountText="(unlimited)"
            subTitle={subTitle}
            title="Share article link"
          />
        </div>

        <ArrowIcon />
      </button>
    );
  }

  return (
    <button
      aria-label="copy-link"
      className="flex w-full items-center justify-between"
      data-test-id="copy-article-link-button"
      type="button"
      onClick={handleCopy}
    >
      <div className="flex items-center">
        <span className="rounded-full bg-orange-60 p-10">
          <LinkIcon className="size-28 fill-share-card-bg-2" />
        </span>

        <LinkTitle
          availableCountText="(unlimited)"
          subTitle={subTitle}
          title="Copy article link"
        />
      </div>

      <CopyIcon />
    </button>
  );
};
